// The webui session rides a SESSION cookie (ratified 2026-07-12): unlike localStorage
// it dies with the browser. It holds the opaque tvss_ token plus the display identity
// the dashboard shows — the cookie is the client's convenience store, the API remains
// the authority (bearer header, spec §3).

export const SESSION_COOKIE = 'tvstor-session'

export type WebuiSession = {
  token: string
  username: string
  realm: string
  displayName: string

  // WEBEXP-KIOSK (ratifica Luca 2026-08-05): 'kiosk' marks a files-realm session —
  // the dashboard layout swaps the platform chrome for the kiosk shell and every
  // route outside /browse redirects there. Absent (older cookies) means console.
  mode?: 'console' | 'kiosk'

  // MFA.2 enrolment warning (#181 ondata 1b): the login 201 carries these two
  // fields — present TOGETHER, only while the credential is marked mfaRequired
  // with no second factor and the grace window is open (contract auth.yaml).
  // Parked here verbatim so the console can KEEP showing what the server
  // affirmed at login; cleared by the profile page the moment the server
  // confirms an enrolment (the warning's own condition has ended).
  mfaEnrollmentDeadline?: string
  mfaEnrollmentDaysLeft?: number
}

// The mode a cookie VALUE carries, decoded defensively — shared by the client
// helpers below and the SERVER dashboard layout (which reads the raw cookie via
// next/headers and must decide the chrome before any client code runs).
export function sessionModeOfCookieValue(value: string | undefined): 'console' | 'kiosk' {
  if (value === undefined) {
    return 'console'
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(value))

    return parsed !== null && typeof parsed === 'object' && parsed.mode === 'kiosk' ? 'kiosk' : 'console'
  } catch {
    return 'console'
  }
}

export function getSession(): WebuiSession | null {
  if (typeof document === 'undefined') {
    return null
  }

  const entry = document.cookie.split('; ').find(part => part.startsWith(`${SESSION_COOKIE}=`))

  if (!entry) {
    return null
  }

  try {
    return JSON.parse(decodeURIComponent(entry.slice(SESSION_COOKIE.length + 1)))
  } catch {
    return null
  }
}

export function setSession(session: WebuiSession) {
  // No expires/max-age: a session cookie, cleared when the browser closes.
  document.cookie = `${SESSION_COOKIE}=${encodeURIComponent(JSON.stringify(session))}; path=/; samesite=lax`
}

export function clearSession() {
  document.cookie = `${SESSION_COOKIE}=; path=/; samesite=lax; max-age=0`
}
